import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { Crown, Trash2, Plus } from "lucide-react"
import type { Family, Permission, RoleWithPermissions, User } from "@mc/types"
import {
  getFamily,
  updateFamily,
  deleteFamily,
  setFamilyMemberRole,
  removeFamilyMember,
  listFamilyRoles,
  createFamilyRole,
  updateFamilyRole,
  deleteFamilyRole,
  type FamilyMemberEntry,
} from "../api/families.js"
import { friendlyError } from "../api/errorMessages.js"
import { Spinner } from "../components/ui/Spinner.js"
import { Button } from "../components/ui/Button.js"
import { Input } from "../components/ui/Input.js"
import { useToast } from "../components/ui/Toast.js"
import { RoleBadge } from "../components/access/RoleBadge.js"
import { RoleEditor } from "../components/access/RoleEditor.js"
import { InviteMember } from "../components/access/InviteMember.js"
import { SwipeToDelete } from "../components/ui/SwipeToDelete.js"
import { useAuth } from "../auth/AuthProvider.js"

type PageState =
  | { status: "loading" }
  | { status: "ready"; family: Family; members: FamilyMemberEntry[]; roles: RoleWithPermissions[] }
  | { status: "error"; message: string }

/**
 * `/families/[id]` (FR section 4 y 8: info, miembros y sus roles dentro
 * del grupo). Edición/gestión gateada server-side por `requireFamilyOwner`
 * (routes/families.ts: solo el owner de la familia o host_owner) — este
 * componente no oculta los controles si el actor no es owner, la 403 de
 * un intento real se muestra como toast, mismo criterio que
 * ServerAccessPage documenta para ACCESS_MANAGE.
 */
export function FamilyDetailPage() {
  const { familyId } = useParams<{ familyId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [state, setState] = useState<PageState>({ status: "loading" })
  const [editingInfo, setEditingInfo] = useState(false)
  const [form, setForm] = useState({ name: "", description: "" })
  const [savingInfo, setSavingInfo] = useState(false)
  const [editingRole, setEditingRole] = useState<RoleWithPermissions | "new" | null>(null)
  const toast = useToast()

  useEffect(() => {
    if (familyId) load(familyId)
  }, [familyId])

  function load(id: string) {
    setState({ status: "loading" })
    Promise.all([getFamily(id), listFamilyRoles(id)])
      .then(([{ family, members }, { roles }]) => {
        setState({ status: "ready", family, members, roles })
        setForm({ name: family.name, description: family.description ?? "" })
      })
      .catch((err) => setState({ status: "error", message: friendlyError(err).message }))
  }

  if (!familyId) return null

  if (state.status === "loading") {
    return (
      <div style={{ display: "grid", placeItems: "center", padding: "var(--space-12) 0" }}>
        <Spinner size="lg" />
      </div>
    )
  }

  if (state.status === "error") {
    return (
      <p role="alert" style={{ color: "var(--status-error)", fontSize: "var(--text-sm)" }}>
        {state.message}
      </p>
    )
  }

  const { family, members, roles } = state
  const isOwner = user?.id === family.ownerId || user?.systemRole === "host_owner"

  async function handleSaveInfo() {
    setSavingInfo(true)
    try {
      const { family: updated } = await updateFamily(familyId!, {
        name: form.name,
        description: form.description || null,
      })
      setState((prev) => (prev.status === "ready" ? { ...prev, family: updated } : prev))
      setEditingInfo(false)
      toast.show("Familia actualizada.", "success")
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    } finally {
      setSavingInfo(false)
    }
  }

  function handleDeleteFamily() {
    toast.confirmDestructive(
      `Se eliminará la familia "${family.name}" y todo su acceso a servidores. Deshacer disponible por 3 segundos.`,
      async () => {
        try {
          await deleteFamily(familyId!)
          navigate("/families")
        } catch (err) {
          toast.show(friendlyError(err).message, "error")
        }
      },
    )
  }

  async function handleSaveRole(input: { name: string; color: string; permissions: Permission[] }) {
    try {
      if (editingRole === "new") {
        await createFamilyRole(familyId!, input)
      } else if (editingRole) {
        await updateFamilyRole(familyId!, editingRole.id, input)
      }
      toast.show("Rol guardado.", "success")
      setEditingRole(null)
      load(familyId!)
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    }
  }

  function handleDeleteRole(role: RoleWithPermissions) {
    toast.confirmDestructive(
      `Se eliminará el rol "${role.name}". Deshacer disponible por 3 segundos.`,
      async () => {
        try {
          await deleteFamilyRole(familyId!, role.id)
          load(familyId!)
        } catch (err) {
          toast.show(friendlyError(err).message, "error")
        }
      },
    )
  }

  async function handleInviteMember(invitedUser: User) {
    if (roles.length === 0) {
      toast.show("Creá al menos un rol antes de agregar miembros.", "error")
      return
    }
    try {
      await setFamilyMemberRole(familyId!, invitedUser.id, roles[0]!.id)
      toast.show(`${invitedUser.username} agregado.`, "success")
      load(familyId!)
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    }
  }

  async function handleSetMemberRole(userId: string, roleId: string) {
    try {
      await setFamilyMemberRole(familyId!, userId, roleId)
      load(familyId!)
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    }
  }

  function handleRemoveMember(member: FamilyMemberEntry) {
    toast.confirmDestructive(
      `Se quitará a ${member.username} de la familia. Deshacer disponible por 3 segundos.`,
      async () => {
        try {
          await removeFamilyMember(familyId!, member.userId)
          load(familyId!)
        } catch (err) {
          toast.show(friendlyError(err).message, "error")
        }
      },
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-8)" }}>
      <div>
        {editingInfo ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", maxWidth: 420 }}>
            <Input label="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input
              label="Descripción"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <div style={{ display: "flex", gap: "var(--space-2)" }}>
              <Button loading={savingInfo} disabled={!form.name} onClick={handleSaveInfo}>
                Guardar
              </Button>
              <Button variant="ghost" disabled={savingInfo} onClick={() => setEditingInfo(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "var(--space-3)" }}>
            <div>
              <h1 style={{ fontSize: "var(--text-3xl)" }}>{family.name}</h1>
              <p style={{ color: "var(--text-muted)" }}>{family.description ?? "Sin descripción."}</p>
            </div>
            {isOwner && (
              <div style={{ display: "flex", gap: "var(--space-2)" }}>
                <Button size="sm" variant="secondary" onClick={() => setEditingInfo(true)}>
                  Editar
                </Button>
                <Button size="sm" variant="destructive" onClick={handleDeleteFamily}>
                  <Trash2 size={14} aria-hidden />
                  Borrar familia
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {isOwner && (
        <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ fontSize: "var(--text-2xl)" }}>Roles</h2>
            {editingRole === null && (
              <Button size="sm" onClick={() => setEditingRole("new")}>
                <Plus size={14} aria-hidden />
                Nuevo rol
              </Button>
            )}
          </div>

          {editingRole !== null && (
            <RoleEditor
              role={editingRole === "new" ? null : editingRole}
              onSave={handleSaveRole}
              onCancel={() => setEditingRole(null)}
            />
          )}

          {roles.length === 0 && editingRole === null && (
            <p style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>
              Todavía no hay roles. Creá uno antes de invitar miembros.
            </p>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            {roles.map((role) => (
              <SwipeToDelete key={role.id} onDelete={() => handleDeleteRole(role)}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "var(--space-3)",
                    padding: "var(--space-3)",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--border-default)",
                    background: "var(--bg-surface)",
                  }}
                >
                  <RoleBadge role={role} />
                  <div style={{ display: "flex", gap: "var(--space-2)" }}>
                    <Button size="sm" variant="secondary" onClick={() => setEditingRole(role)}>
                      Editar
                    </Button>
                    <Button size="sm" variant="destructive" aria-label={`Borrar rol ${role.name}`} onClick={() => handleDeleteRole(role)}>
                      <Trash2 size={14} aria-hidden />
                    </Button>
                  </div>
                </div>
              </SwipeToDelete>
            ))}
          </div>
        </section>
      )}

      <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <h2 style={{ fontSize: "var(--text-2xl)" }}>Miembros</h2>
        {isOwner && <InviteMember onInvite={handleInviteMember} actionLabel="Agregar miembro" />}

        {members.length === 0 ? (
          <p style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>Todavía no hay miembros.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            {members.map((member) => {
              const role = roles.find((r) => r.id === member.roleId) ?? null
              const isOwnerRow = member.userId === family.ownerId
              const row = (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "var(--space-3)",
                    padding: "var(--space-3)",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--border-default)",
                    background: "var(--bg-surface)",
                    flexWrap: "wrap",
                  }}
                >
                  <span style={{ fontSize: "var(--text-sm)" }}>{member.displayName ?? member.username}</span>
                  {isOwnerRow ? (
                    // The family owner's membership is implicit ownership, not
                    // a reassignable role. No observer (not the owner
                    // themselves, not a delegate with member-management
                    // rights) gets the role select or the remove button for
                    // this row — same rule the Gateway enforces server-side
                    // in routes/families.ts.
                    <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                      <Crown size={16} aria-hidden style={{ color: "var(--brand-primary)" }} />
                      <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>Owner</span>
                    </div>
                  ) : isOwner ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
                      <select
                        value={member.roleId}
                        onChange={(e) => handleSetMemberRole(member.userId, e.target.value)}
                        style={{
                          padding: "6px 8px",
                          fontSize: "var(--text-sm)",
                          borderRadius: "var(--radius-sm)",
                          border: "1px solid var(--border-default)",
                          background: "var(--bg-surface)",
                          color: "var(--text-primary)",
                        }}
                      >
                        {roles.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.name}
                          </option>
                        ))}
                      </select>
                      <Button
                        size="sm"
                        variant="destructive"
                        aria-label={`Quitar a ${member.displayName ?? member.username}`}
                        onClick={() => handleRemoveMember(member)}
                      >
                        <Trash2 size={14} aria-hidden />
                      </Button>
                    </div>
                  ) : (
                    <RoleBadge role={role} />
                  )}
                </div>
              )
              // Swipe-to-delete only makes sense where a delete action
              // actually exists: the owner's row has none, and a
              // non-owner viewer has no destructive control on any row.
              return isOwner && !isOwnerRow ? (
                <SwipeToDelete key={member.userId} onDelete={() => handleRemoveMember(member)}>
                  {row}
                </SwipeToDelete>
              ) : (
                <div key={member.userId}>{row}</div>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}
